import { Link } from 'react-router-dom';
import { useState } from 'react';

const BG = '#0A0B14';
const CARD = '#14162A';
const BORDER = '#262A3F';
const TEXT = '#E2E8F0';
const MUTED = '#94A3B8';
const VIOLET = '#8B5CF6';
const VIOLET_LIGHT = '#A78BFA';
const GOOD = '#2F855A';
const FONT = "'DM Sans', system-ui, -apple-system, sans-serif";

// Suggestions are delivered by a Cloudflare Worker at /api/suggest
// (the Worker emails them to suggestions@nerdsuite.app, which
// Cloudflare Email Routing forwards to Ian's Proton).
//
// DESIGN (user ease first): the PRIMARY path is a real form POST —
// the user types and taps Submit, no mail client, works on every
// device. There is deliberately NO mailto: (it silently fails on
// phones/Chromebooks with no mail app — verified broken on Ian's own
// PC). If the POST fails for any reason (offline, Worker down, route
// not yet deployed), we NEVER silently lose the suggestion: a
// copyable address fallback always appears so the user can still send
// it manually. Form first, fallback always present.
const FEEDBACK_EMAIL = 'suggestions@nerdsuite.app';
const ENDPOINT = '/api/suggest';

export default function Suggest() {
  const [text, setText] = useState('');
  // V65.1 — honeypot: a hidden field real users never fill. Bots that
  // auto-fill every input will populate it, and the /api/suggest
  // handler silently drops any submission where it's non-empty.
  const [website, setWebsite] = useState('');
  // status: 'idle' | 'sending' | 'sent' | 'error'
  const [status, setStatus] = useState('idle');
  const [copied, setCopied] = useState(false);
  const canSubmit = text.trim().length >= 3 && status !== 'sending';

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setStatus('sending');
    try {
      const res = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ suggestion: text.trim(), website }),
      });
      if (res.ok) {
        setStatus('sent');
      } else {
        setStatus('error');
      }
    } catch {
      // Network failure / endpoint not reachable → show the fallback,
      // do not pretend it sent.
      setStatus('error');
    }
  };

  const handleCopy = async () => {
    const payload = `To: ${FEEDBACK_EMAIL}\nSubject: NerdSuite: tool suggestion\n\n${text.trim()}`;
    let ok = false;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(payload);
        ok = true;
      }
    } catch { ok = false; }
    if (!ok) {
      try {
        const ta = document.createElement('textarea');
        ta.value = payload;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus(); ta.select();
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch { ok = false; }
    }
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2600);
    } else {
      window.prompt('Copy this, then email it from anywhere:', payload);
    }
  };

  const sent = status === 'sent';

  return (
    <div style={{
      minHeight: '100vh',
      background: BG,
      color: TEXT,
      fontFamily: FONT,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '32px 24px',
    }}>
      <Link to="/" style={{
        position: 'absolute',
        top: 24,
        left: 24,
        color: VIOLET_LIGHT,
        textDecoration: 'none',
        fontSize: 14,
        fontWeight: 600,
      }}>
        ← NerdSuite
      </Link>

      <div style={{ maxWidth: 560, width: '100%', textAlign: 'center' }}>
        <div style={{
          width: 100, height: 100,
          margin: '0 auto 24px',
          background: sent ? GOOD : VIOLET,
          borderRadius: 22,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 64,
          fontWeight: 300,
          color: '#ffffff',
          lineHeight: 1,
          transition: 'background 0.25s',
        }}>
          <span style={{ marginTop: '-0.08em' }}>{sent ? '✓' : '+'}</span>
        </div>

        <h1 style={{
          margin: 0,
          fontSize: 'clamp(32px, 5vw, 48px)',
          fontWeight: 800,
          letterSpacing: -1,
        }}>
          {sent ? 'Got it — thank you!' : 'What should we build next?'}
        </h1>

        {sent ? (
          <p style={{ marginTop: 16, fontSize: 17, color: MUTED, lineHeight: 1.5 }}>
            Your suggestion is in. The best ideas get built first — keep
            an eye on the site.
          </p>
        ) : (
          <>
            <p style={{
              marginTop: 16,
              marginBottom: 32,
              fontSize: 17,
              color: MUTED,
              lineHeight: 1.5,
            }}>
              Got a tool you wish existed? Tell us. We're listening, and
              the best ideas get built first.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* V65.1 — honeypot. Visually hidden (not display:none, which
                  some bots skip), off-screen, not tab-reachable, autocomplete
                  off. Real users never see or fill it; bots that fill every
                  field trip it and /api/suggest silently drops the submission. */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                style={{
                  position: 'absolute',
                  left: '-9999px',
                  width: 1,
                  height: 1,
                  opacity: 0,
                  pointerEvents: 'none',
                }}
              />
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="e.g. A citation generator for MLA / APA / Chicago that doesn't require an account..."
                rows={5}
                style={{
                  width: '100%',
                  padding: 16,
                  background: CARD,
                  border: `1px solid ${BORDER}`,
                  borderRadius: 12,
                  color: TEXT,
                  fontSize: 15,
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  outline: 'none',
                  transition: 'border-color 0.18s',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => { e.target.style.borderColor = VIOLET; }}
                onBlur={(e) => { e.target.style.borderColor = BORDER; }}
              />
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                style={{
                  padding: '14px 24px',
                  background: !canSubmit ? '#3A3D55' : VIOLET,
                  color: 'white',
                  border: 'none',
                  borderRadius: 12,
                  fontSize: 15,
                  fontWeight: 600,
                  fontFamily: 'inherit',
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                  transition: 'background 0.18s',
                }}
                onMouseEnter={(e) => { if (canSubmit) e.currentTarget.style.background = VIOLET_LIGHT; }}
                onMouseLeave={(e) => { if (canSubmit) e.currentTarget.style.background = VIOLET; }}
              >
                {status === 'sending' ? 'Sending…' : 'Send suggestion →'}
              </button>
            </div>

            {/* Fallback — appears only if the POST failed, so a
                suggestion is never silently lost. Also the permanent
                manual option. */}
            {status === 'error' && (
              <div style={{
                marginTop: 24,
                padding: '16px 18px',
                background: CARD,
                border: `1px solid ${BORDER}`,
                borderRadius: 12,
                textAlign: 'left',
              }}>
                <p style={{ margin: '0 0 10px', fontSize: 13.5, color: '#F0A878', lineHeight: 1.5 }}>
                  Couldn't send that automatically. No problem — copy it
                  and email it to us directly:
                </p>
                <button
                  type="button"
                  onClick={handleCopy}
                  style={{
                    padding: '10px 16px',
                    marginBottom: 12,
                    background: copied ? GOOD : VIOLET,
                    color: 'white',
                    border: 'none',
                    borderRadius: 10,
                    fontSize: 14,
                    fontWeight: 600,
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                  }}
                >
                  {copied ? '✓ Copied' : 'Copy my suggestion'}
                </button>
                <p style={{ margin: '0 0 6px', fontSize: 13, color: MUTED }}>
                  Send it to:
                </p>
                <code style={{
                  display: 'block',
                  fontSize: 15,
                  fontWeight: 600,
                  color: VIOLET_LIGHT,
                  userSelect: 'all',
                  wordBreak: 'break-all',
                }}>
                  {FEEDBACK_EMAIL}
                </code>
              </div>
            )}

            <p style={{
              marginTop: 20,
              fontSize: 13,
              color: MUTED,
            }}>
              No account, no tracking.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
